import { FormEvent, useEffect, useState } from 'react'
import { Button } from '../components/button'
import {
	OfferPasswordRole,
	useUserOfferPasswordStatus,
	useUserOfferPasswordSet,
	useUserOfferPasswordSetRole,
	useUserOfferPasswordClear,
} from '../api/users'

type Props = {
	userId: string
}

const ROLE_LABEL: Record<OfferPasswordRole, string> = {
	user: 'Pokladna (user)',
	admin: 'Admin',
}

const ROLE_HINT: Record<OfferPasswordRole, string> = {
	user: 'Standardní rozhraní pokladny — vyplňuje výdej a vratku, požadavky čekají na schválení.',
	admin: 'Rozšířené rozhraní pro vedoucího — může schvalovat sebe i ostatní v ATD.',
}

/**
 * Admin-only UI block for setting / clearing a user's fixed password used to
 * log into vehys-offer (výdej / vratka). The password itself is never read
 * back from the server — only its presence, last update timestamp and role.
 */
export function UsersOfferPassword({ userId }: Props) {
	const { data: status } = useUserOfferPasswordStatus(userId)
	const setPassword = useUserOfferPasswordSet(userId)
	const setRole = useUserOfferPasswordSetRole(userId)
	const clearPassword = useUserOfferPasswordClear(userId)

	const [password, setPasswordValue] = useState('')
	const [confirm, setConfirmValue] = useState('')
	const [role, setRoleValue] = useState<OfferPasswordRole>('user')
	const [localError, setLocalError] = useState<string | null>(null)

	// Při načtení statusu prefilluj výběr role aktuální hodnotou ze serveru,
	// aby admin viděl, co je nastaveno, a aby případná samostatná změna role
	// (bez přepisu hesla) odpovídala stavu v DB.
	useEffect(() => {
		if (status?.role) {
			setRoleValue(status.role)
		}
	}, [status?.role])

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setLocalError(null)

		const next = password.trim()
		if (next.length < 4) {
			setLocalError('Heslo musí mít alespoň 4 znaky.')
			return
		}
		if (next !== confirm.trim()) {
			setLocalError('Hesla se neshodují.')
			return
		}

		setPassword.mutate(
			{ password: next, role },
			{
				onSuccess: () => {
					setPasswordValue('')
					setConfirmValue('')
				},
			},
		)
	}

	const onChangeRoleOnly = () => {
		if (!status?.has_password) return
		if (status.role === role) return
		setRole.mutate(role)
	}

	const onClear = () => {
		if (!status?.has_password) return
		clearPassword.mutate()
	}

	const inputClass =
		'border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-400'

	const roleChanged = !!status?.has_password && status.role !== role

	return (
		<section className="space-y-4 m-auto max-w-[60em] mt-8 border-t pt-6">
			<header className="space-y-1">
				<h3 className="text-md font-bold">Heslo do výdejny / vratky (vehys-offer)</h3>
				<p className="text-xs text-gray-600">
					Tento uživatel se s tímto heslem přihlásí do aplikace vehys-offer. Heslo nelze
					později zobrazit — pouze přepsat nebo zrušit.
				</p>
				<p className="text-xs text-gray-700">
					{status?.has_password
						? `Heslo je nastavené${
								status.updated_at
									? ` (naposledy ${new Date(status.updated_at).toLocaleString('cs-CZ')})`
									: ''
							}${status.role ? ` jako ${ROLE_LABEL[status.role]}` : ''}.`
						: 'Heslo zatím není nastavené — bez něj se uživatel do vehys-offer nepřihlásí.'}
				</p>
			</header>

			<form onSubmit={onSubmit} className="grid grid-cols-2 gap-4 items-end">
				<label className="flex flex-col gap-1">
					<span className="text-xs text-gray-600">Nové heslo</span>
					<input
						className={inputClass}
						type="password"
						autoComplete="new-password"
						value={password}
						onChange={event => setPasswordValue(event.target.value)}
						minLength={4}
						maxLength={128}
						required
					/>
				</label>
				<label className="flex flex-col gap-1">
					<span className="text-xs text-gray-600">Potvrdit heslo</span>
					<input
						className={inputClass}
						type="password"
						autoComplete="new-password"
						value={confirm}
						onChange={event => setConfirmValue(event.target.value)}
						minLength={4}
						maxLength={128}
						required
					/>
				</label>
				<fieldset className="col-span-2 flex flex-col gap-2">
					<span className="text-xs text-gray-600">Role v ATD / vehys-offer</span>
					<div className="flex flex-wrap gap-4">
						{(Object.keys(ROLE_LABEL) as OfferPasswordRole[]).map(option => (
							<label
								key={option}
								className="flex items-start gap-2 cursor-pointer text-sm"
							>
								<input
									type="radio"
									name={`offer-role-${userId}`}
									value={option}
									checked={role === option}
									onChange={() => setRoleValue(option)}
									className="mt-1"
								/>
								<span>
									<span className="font-semibold">{ROLE_LABEL[option]}</span>
									<span className="block text-xs text-gray-500">
										{ROLE_HINT[option]}
									</span>
								</span>
							</label>
						))}
					</div>
				</fieldset>
				{localError ? (
					<div className="col-span-2 text-xs text-rose-600">{localError}</div>
				) : null}
				<div className="col-span-2 flex flex-wrap gap-3">
					<Button
						type="submit"
						isPending={setPassword.isPending}
						isDisabled={setPassword.isPending}
					>
						{status?.has_password ? 'Změnit heslo' : 'Nastavit heslo'}
					</Button>
					{status?.has_password ? (
						<>
							<Button
								type="button"
								variant="SECONDARY"
								onPress={onChangeRoleOnly}
								isPending={setRole.isPending}
								isDisabled={!roleChanged || setRole.isPending}
							>
								{roleChanged ? `Uložit roli (${ROLE_LABEL[role]})` : 'Role je aktuální'}
							</Button>
							<Button
								type="button"
								variant="DANGER"
								onPress={onClear}
								isPending={clearPassword.isPending}
								isDisabled={clearPassword.isPending}
							>
								Zrušit heslo
							</Button>
						</>
					) : null}
				</div>
			</form>
		</section>
	)
}
